import { Icon } from '@chakra-ui/react'
import {
  Activity,
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  Download,
  ExternalLink,
  LogOut,
  MessageSquare,
  Settings,
  Sparkles,
} from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import MangoLogo from '~/assets/svgs/icon-mango-logo.svg?react'
import iconSwagger from '~/assets/svgs/icon-swagger.svg?react'
import { useUserState } from '~/atoms/UserState'
import { DropdownMangoButton } from '~/components/shared/Dropdown/DropdownMangoButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { MangoButtonVariant } from '~/components/shared/MangoButton'
import { SwitchPbxModal } from '~/components/shared/PbxPicker'
import urls from '~/constants/urls'
import { useChangelogModal } from '~/hooks/useChangelogModal'
import { usePartnerDesktopAppDownloadUrl } from '~/hooks/useCurrentPartner'
import { useLogout } from '~/hooks/useLogout'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { TranslationKeys } from '~/locales/i18next'
import { HealthIndicatorIcon } from '~/pages/Status/HealthIndicatorIcon'
import { setStatusReturnTo } from '~/pages/Status/StatusPanel'
import { useOverallHealth } from '~/pages/Status/useOverallHealth'
import { useGetPbxList } from '~/queries/pbxQueries'
import { Environment } from '~/types/EnvironmentTypes'
import { buildEnvironment, getApiDocsUrl, isElectron } from '~/utils/common'
import { buildDeepLink, openDesktopApp } from '~/utils/desktopDeepLink'
import { toNounCasing } from '~/utils/formatter'
import { openInNewTab } from '~/utils/functions'

import { useAccountButtonStyle } from './componentStyles'
import { AccountButtonStyleValues } from './componentStyles/AccountButton.style'

// Reads health internally so it re-renders on connection changes without
// pulling overallHealth into AccountButton's menuItemData memo (which would
// rebuild the whole menu on every reconnect attempt).
const ConnectionStatusHealthIcon = () => {
  const overallHealth = useOverallHealth()
  return <HealthIndicatorIcon level={overallHealth} />
}

export const AccountButton = () => {
  const { t } = useTranslation()
  const { sessionUser, pbx } = useUserState()
  const firstLast = !sessionUser
    ? ''
    : toNounCasing(`${sessionUser.first_name} ${sessionUser.last_name}`)
  const handleLogout = useLogout()
  const desktopAppDownloadUrl = usePartnerDesktopAppDownloadUrl()
  const { showChangelog, ChangelogModal } = useChangelogModal()
  // Ref so the menu-item onClick reads the latest showChangelog without it
  // becoming a useMemo dep (the hook returns a fresh callback each render,
  // which would otherwise rebuild the whole menu).
  const showChangelogRef = React.useRef(showChangelog)
  showChangelogRef.current = showChangelog

  const navigate = useNavigate()
  const location = useLocation()
  // Keep the current router location in a ref so the menu-item onClick reads it
  // without `location` becoming a useMemo dep (which would rebuild the menu on
  // every navigation). Using the router location rather than window.location
  // avoids capturing a stale URL during a pending navigation.
  const locationRef = React.useRef(location)
  locationRef.current = location
  const accountButtonRef = React.useRef<HTMLDivElement>(null)
  const [isMenuVisible, setIsMenuVisible] = React.useState(false)
  const [isSwitchPbxModalOpen, setIsSwitchPbxModalOpen] = useState(false)

  const { data: pbxList } = useGetPbxList({
    apiParams: { limit: 1 },
  })
  const hasMultiplePbxs = (pbxList?.count ?? 0) > 1

  const styles = useAccountButtonStyle()

  const getMenuItem = (
    primaryText: string,
    leftIcon: React.ComponentType<any>,
    onClick: () => void,
    rightIcon?: React.ComponentType<any>,
    hide?: boolean,
    secondaryText?: string,
    secondaryTextClamp?: number,
    secondaryTextProps?: Record<string, unknown>,
    leftNode?: React.ReactNode,
  ) => {
    return {
      value: primaryText,
      content: (
        <MenuListItem
          primaryText={primaryText}
          secondaryText={secondaryText}
          secondaryTextClamp={secondaryTextClamp}
          secondaryTextProps={secondaryTextProps}
          leftContent={leftNode ?? <Icon as={leftIcon} css={styles.leftIcon} />}
          rightContent={
            rightIcon && <Icon as={rightIcon} css={styles.rightIcon} />
          }
          hide={hide}
        />
      ),
      onSelect: onClick,
    } as MenuItem<string>
  }

  const handleMenuOpenChange = (isOpen: boolean) => {
    setIsMenuVisible(isOpen)
  }

  const menuItemData = useMemo(() => {
    const list: {
      onClick: () => void | Promise<void>
      icon: React.ComponentType<any>
      text: string
      rightIcon?: React.ComponentType<any>
      hide?: boolean
      secondaryText?: string
      secondaryTextClamp?: number
      secondaryTextProps?: Record<string, unknown>
      leftNode?: React.ReactNode
    }[] = [
      {
        onClick: () => navigate(ApplicationRoutes.SETTINGS),
        icon: Settings,
        text: 'accountButton.settings',
      },
      {
        onClick: () => setIsSwitchPbxModalOpen(true),
        icon: ArrowRightLeft,
        text: 'accountButton.switchPbx',
        hide: !hasMultiplePbxs,
        secondaryText: pbx?.name,
        secondaryTextClamp: 1,
        secondaryTextProps: { fontSize: 'xs' },
      },
      {
        onClick: () => openDesktopApp(buildDeepLink('settings')),
        icon: MangoLogo,
        text: 'accountButton.openApp',
        rightIcon: ExternalLink,
        hide: isElectron,
      },
      {
        onClick: () => openInNewTab(desktopAppDownloadUrl),
        icon: Download,
        text: 'accountButton.downloadApp',
        rightIcon: ExternalLink,
        hide: isElectron,
      },
      {
        onClick: () => showChangelogRef.current(),
        icon: Sparkles,
        text: 'accountButton.whatsNew',
        // Changelog data is only available over Electron IPC; on web the modal
        // has nothing to show, so match ProfileSettings and hide it there.
        hide: !isElectron,
      },
      {
        onClick: () => openInNewTab(urls.FEEDBACK),
        icon: MessageSquare,
        text: 'accountButton.giveFeedback',
        rightIcon: ExternalLink,
      },
      {
        onClick: () => openInNewTab(urls.HELP_CENTER),
        icon: CircleHelp,
        text: 'accountButton.helpCenter',
        rightIcon: ExternalLink,
      },
      {
        onClick: () => {
          const loc = locationRef.current
          setStatusReturnTo(
            loc.pathname + loc.search,
            accountButtonRef.current?.querySelector('button'),
          )
          navigate(ApplicationRoutes.STATUS)
        },
        icon: Activity,
        text: 'accountButton.connectionStatus',
        // Shared health glyph so the menu reads identically to the top-nav
        // chip. Self-contained so health changes don't rebuild the menu.
        leftNode: <ConnectionStatusHealthIcon />,
      },
      {
        onClick: () => {
          void handleLogout()
        },
        icon: LogOut,
        text: 'accountButton.logOut',
      },
    ]

    if (buildEnvironment === Environment.LOCAL) {
      // Insert just before App status (and Log Out) rather than at a
      // fixed offset, so reordering the trailing items can't misplace this.
      const anchorIdx = list.findIndex(
        (i) => i.text === 'accountButton.connectionStatus',
      )
      const insertAt = anchorIdx === -1 ? list.length : anchorIdx
      list.splice(insertAt, 0, {
        onClick: () => openInNewTab(getApiDocsUrl()),
        icon: iconSwagger,
        text: 'accountButton.apiDocs',
        rightIcon: ExternalLink,
      })
    }

    return list
  }, [
    handleLogout,
    hasMultiplePbxs,
    pbx?.name,
    navigate,
    desktopAppDownloadUrl,
  ])

  const menuItems = useMemo(() => {
    return menuItemData.map((item) => {
      return getMenuItem(
        t(item.text as TranslationKeys),
        item.icon,
        item.onClick,
        item.rightIcon,
        item.hide ?? false,
        item.secondaryText,
        item.secondaryTextClamp,
        item.secondaryTextProps,
        item.leftNode,
      )
    })
  }, [menuItemData, t])

  return (
    <>
      <div ref={accountButtonRef} data-testid="account-button">
        <DropdownMangoButton
          variant={MangoButtonVariant.Default_200}
          label={firstLast}
          rightIcon={isMenuVisible ? ChevronUp : ChevronDown}
          mangoButtonOptions={{
            rightIconProps: {
              boxSize: AccountButtonStyleValues.chevronBoxSize,
            },
          }}
          items={menuItems}
          onMenuOpenChange={handleMenuOpenChange}
          menuOptions={{
            placement: MenuPlacement.BottomRight,
            width: AccountButtonStyleValues.menuWidth,
            maxHeight: AccountButtonStyleValues.menuMaxHeight,
          }}
        />
      </div>
      <SwitchPbxModal
        open={isSwitchPbxModalOpen}
        onClose={() => setIsSwitchPbxModalOpen(false)}
      />
      {ChangelogModal}
    </>
  )
}
